// src/mocks/marketDataProvider.ts
import { MarketData, MarketDataPoint, TimeFrame } from './tradingTypes';

// Cache para guardar datos generados
let dataCache: Record<string, MarketData> = {};

/**
 * Obtiene datos de mercado simulados para un símbolo y timeframe específicos
 */
export const getMockMarketData = (symbol: string, timeframe: TimeFrame = '1h'): MarketData => {
  const cacheKey = `${symbol}-${timeframe}`;

  // Si ya tenemos datos para este símbolo/timeframe, devolverlos
  if (dataCache[cacheKey]) {
    return dataCache[cacheKey];
  }

  // Generar datos según el símbolo
  const basePrice = getBasePrice(symbol);
  const volatility = getVolatility(symbol);
  const data = generatePriceData(basePrice, volatility, timeframe, symbol);

  // Guardar en cache
  dataCache[cacheKey] = {
    symbol,
    timeframe,
    data,
    lastUpdate: new Date()
  };

  return dataCache[cacheKey];
};

/**
 * Actualiza los datos de mercado simulados añadiendo un nuevo punto de datos
 */
export const updateMockMarketData = (symbol: string, timeframe: TimeFrame = '1h'): MarketData => {
  const cacheKey = `${symbol}-${timeframe}`;
  const existingData = dataCache[cacheKey] || getMockMarketData(symbol, timeframe);

  // Obtener último precio
  const lastDataPoint = existingData.data[existingData.data.length - 1];
  const lastPrice = lastDataPoint.price;
  const volatility = getVolatility(symbol);

  // Calcular nuevo precio
  const trend = Math.sin(Date.now() / 10000000) * 0.3; // Tendencia suave basada en tiempo
  const randomWalk = (Math.random() - 0.5) * 2 * volatility;
  const change = (randomWalk + trend * volatility) * lastPrice;
  const newPrice = lastPrice + change;

  // Generar high y low realistas
  const dailyVolatility = volatility * newPrice;
  const high = Math.max(newPrice, lastPrice) + (Math.random() * dailyVolatility * 0.2);
  const low = Math.min(newPrice, lastPrice) - (Math.random() * dailyVolatility * 0.2);

  // Crear nuevo punto de datos
  const newTimestamp = new Date(
    new Date(lastDataPoint.timestamp).getTime() + getTimeframeMilliseconds(timeframe)
  ).toISOString();

  // Crear nuevo punto de datos
  const newDataPoint: MarketDataPoint = {
    timestamp: newTimestamp,
    price: newPrice,
    volume: Math.random() * getBaseVolume(symbol) * (1 + Math.abs(trend)),
    high,
    low,
    open: lastPrice,
    close: newPrice
  };

  // Actualizar array de datos
  const updatedData = [...existingData.data, newDataPoint];

  // Si el array es muy grande, eliminar elementos antiguos
  if (updatedData.length > getMaxDataPoints(timeframe)) {
    updatedData.shift();
  }

  // Crear objeto actualizado
  const updatedMarketData: MarketData = {
    ...existingData,
    data: updatedData,
    lastUpdate: new Date()
  };

  // Guardar en cache
  dataCache[cacheKey] = updatedMarketData;

  return updatedMarketData;
};

/**
 * Limpia el caché de datos
 */
export const clearMarketDataCache = (): void => {
  dataCache = {};
};

/**
 * Devuelve el precio base para un símbolo
 */
function getBasePrice(symbol: string): number {
  switch (symbol) {
    case 'BTC/USD': return 55000;
    case 'ETH/USD': return 3000;
    case 'XRP/USD': return 0.5;
    case 'SOL/USD': return 100;
    case 'ADA/USD': return 1.2;
    case 'DOT/USD': return 20;
    case 'DOGE/USD': return 0.1;
    case 'LINK/USD': return 15;
    case 'AVAX/USD': return 50;
    case 'MATIC/USD': return 0.8;
    default: return 100;
  }
}

/**
 * Devuelve la volatilidad para un símbolo
 */
function getVolatility(symbol: string): number {
  switch (symbol) {
    case 'BTC/USD': return 0.02; // 2%
    case 'ETH/USD': return 0.025; // 2.5%
    case 'XRP/USD': return 0.03; // 3%
    case 'SOL/USD': return 0.035; // 3.5%
    case 'ADA/USD': return 0.028; // 2.8%
    case 'DOT/USD': return 0.032; // 3.2%
    case 'DOGE/USD': return 0.045; // 4.5%
    case 'LINK/USD': return 0.027; // 2.7%
    case 'AVAX/USD': return 0.033; // 3.3%
    case 'MATIC/USD': return 0.035; // 3.5%
    default: return 0.02;
  }
}

/**
 * Devuelve el volumen base para un símbolo
 */
function getBaseVolume(symbol: string): number {
  switch (symbol) {
    case 'BTC/USD': return 1000;
    case 'ETH/USD': return 5000;
    case 'XRP/USD': return 10000;
    case 'SOL/USD': return 3000;
    case 'ADA/USD': return 8000;
    case 'DOT/USD': return 2000;
    case 'DOGE/USD': return 15000;
    case 'LINK/USD': return 2500;
    case 'AVAX/USD': return 1800;
    case 'MATIC/USD': return 7000;
    default: return 1000;
  }
}

/**
 * Devuelve el número máximo de puntos de datos para un timeframe
 */
function getMaxDataPoints(timeframe: string): number {
  switch (timeframe) {
    case '1m': return 120; // 2 horas
    case '5m': return 288; // 24 horas
    case '15m': return 192; // 48 horas
    case '30m': return 96; // 48 horas
    case '1h': return 168; // 7 días
    case '4h': return 180; // 30 días
    case '1d': return 90; // 3 meses
    case '1w': return 52; // 1 año
    default: return 100;
  }
}

/**
 * Devuelve el intervalo de tiempo en milisegundos para un timeframe
 */
function getTimeframeMilliseconds(timeframe: string): number {
  switch (timeframe) {
    case '1m': return 60 * 1000;
    case '5m': return 5 * 60 * 1000;
    case '15m': return 15 * 60 * 1000;
    case '30m': return 30 * 60 * 1000;
    case '1h': return 60 * 60 * 1000;
    case '4h': return 4 * 60 * 60 * 1000;
    case '1d': return 24 * 60 * 60 * 1000;
    case '1w': return 7 * 24 * 60 * 60 * 1000;
    default: return 60 * 60 * 1000; // 1h por defecto
  }
}

/**
 * Genera un conjunto de datos de precios históricos simulados
 */
function generatePriceData(basePrice: number, volatility: number, timeframe: string, symbol: string): MarketDataPoint[] {
  const count = getMaxDataPoints(timeframe);
  const data: MarketDataPoint[] = [];
  let price = basePrice;

  // Determinar intervalo de tiempo
  const interval = getTimeframeMilliseconds(timeframe);

  const now = new Date();
  let prevPrice = price;

  for (let i = 0; i < count; i++) {
    // Simular movimiento de precio con tendencia
    const trend = Math.sin(i / (count / 6)) * 0.3; // Ciclo sinusoidal más largo
    const randomWalk = (Math.random() - 0.5) * 2 * volatility;
    const change = (randomWalk + trend * volatility) * price;
    price += change;

    // Asegurar que el precio no caiga por debajo de un mínimo
    price = Math.max(price, basePrice * 0.1);

    // Calcular timestamp restando tiempo según el intervalo
    const timestamp = new Date(now.getTime() - (count - i) * interval).toISOString();

    // Generar high y low realistas
    const dailyVolatility = volatility * price;
    const high = Math.max(price, prevPrice) + (Math.random() * dailyVolatility * 0.5);
    const low = Math.min(price, prevPrice) - (Math.random() * dailyVolatility * 0.5);

    // Crear punto de datos
    const dataPoint: MarketDataPoint = {
      timestamp,
      price,
      volume: Math.random() * getBaseVolume(symbol) * (1 + Math.abs(trend)),
      high,
      low,
      open: prevPrice,
      close: price
    };

    data.push(dataPoint);
    prevPrice = price;
  }

  return data;
}
